import { useState } from "react";
import { GripVertical, ChevronDown, Settings2 } from "lucide-react";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { CvSectionBlock, SectionKey, SectionConfigMap, CV_SECTION_META } from "@/types/cvTemplate";
import { CvSectionConfigPanel } from "./CvSectionConfigPanel";

interface CvSectionCardProps {
	block: CvSectionBlock;
	onToggle: (key: SectionKey, enabled: boolean) => void;
	onConfigChange: (key: SectionKey, config: SectionConfigMap[SectionKey]) => void;
	isDragTarget?: boolean;
	disabled?: boolean;
	onDragStart?: (e: React.DragEvent) => void;
	onDragOver?: (e: React.DragEvent) => void;
	onDrop?: (e: React.DragEvent) => void;
	onDragEnd?: () => void;
}

function getSectionIcon(iconName: string) {
	const IconComponent = (Icons as any)[iconName];
	return IconComponent || Icons.FileText;
}

export function CvSectionCard({
	block,
	onToggle,
	onConfigChange,
	isDragTarget,
	disabled,
	onDragStart,
	onDragOver,
	onDrop,
	onDragEnd,
}: CvSectionCardProps) {
	const [expanded, setExpanded] = useState(false);
	const meta = CV_SECTION_META[block.key];
	const Icon = getSectionIcon(meta.icon);

	const hasConfig = block.key !== "education";

	return (
		<div
			draggable
			onDragStart={onDragStart}
			onDragOver={onDragOver}
			onDrop={onDrop}
			onDragEnd={onDragEnd}
			className={cn(
				"border rounded-sm bg-card transition-all",
				isDragTarget && "border-blue-500 border-2",
				!block.enabled && "opacity-50"
			)}
		>
			<div className="flex items-center gap-2 px-3 py-2.5">
				{/* Drag handle */}
				<div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors">
					<GripVertical className="h-4 w-4" />
				</div>

				{/* Icon + Label */}
				<Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
				<span className={cn(
					"text-sm font-medium flex-1",
					!block.enabled && "text-muted-foreground"
				)}>
					{meta.label}
				</span>

				{/* Config expand button */}
				{hasConfig && block.enabled && (
					<button
						type="button"
						onClick={() => setExpanded(!expanded)}
						className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
					>
						{expanded
							? <ChevronDown className="h-3.5 w-3.5" />
							: <Settings2 className="h-3.5 w-3.5" />
						}
					</button>
				)}

				{/* Enable toggle */}
				<Switch
					checked={block.enabled}
					onCheckedChange={(enabled) => {
						onToggle(block.key, enabled);
						if (!enabled) setExpanded(false);
					}}
					disabled={disabled}
				/>
			</div>

			{/* Config panel */}
			{hasConfig && block.enabled && expanded && (
				<div className="px-4 pb-3 pt-1 border-t bg-muted/30">
					<CvSectionConfigPanel
						sectionKey={block.key}
						config={block.config}
						onConfigChange={(config) => onConfigChange(block.key, config)}
						disabled={disabled}
					/>
				</div>
			)}
		</div>
	);
}
